import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Save,
  Plus,
  User,
  Building2,
  ShoppingCart,
  Calendar,
  FileText,
  Mail,
  Phone,
  MapPin,
  ShoppingBag,
  Download,
  CheckCircle,
  Loader2,
  X,
} from 'lucide-react';
import salesService from '../services/salesService';
import purchaseService from '../services/purchaseService';
import clientService from '../services/clientService';
import authService from '../services/authService';
import fileService from '../services/fileService';
import Layout from '../components/Layout';
import EnhancedProductLookup from '../components/EnhancedProductLookup';
import ProductTable from '../components/ProductTable';
import PriceHistoryDrawer from '../components/PriceHistoryDrawer';
import TemplateSelector from '../components/TemplateSelector';
import ProductAttachmentPreviewDialog from '../components/ProductAttachmentPreviewDialog';
import excelExportService from '../services/excelExportService';
import ClientSelector from '../components/ClientSelector';
import PricingModeSelector from '../components/PricingModeSelector';
import SyncProductsButton from '../components/SyncProductsButton';
import useBlockNavigation from '../hooks/useBlockNavigation';

const SalesFormPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const isCloneMode = Boolean(location.state?.clonedOrder);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Client selection states
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientData, setSelectedClientData] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    sales_order_id: `SO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
    order_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    payment_terms: '',
    notes: '',
    status: 'draft'
  });

  const [products, setProducts] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  // Replace product dialog state
  const [replaceDialogState, setReplaceDialogState] = useState({
    open: false,
    replacingIndex: null,
    currentProduct: null
  });
  // Pricing mode for price entry and calculations
  const [pricingMode, setPricingMode] = useState('tax_exclusive');
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);

  // Price history drawer state
  const [priceHistoryDrawer, setPriceHistoryDrawer] = useState({
    open: false,
    productId: '',
    productName: '',
    tipDiameter: '',
    totalLength: '',
    category: '',
    lineItemIndex: null // Track the specific line item index to update
  });

  // Attachment states
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const [attachmentDialog, setAttachmentDialog] = useState({
    open: false,
    product: null
  });

  // Form validation states
  const [fieldErrors, setFieldErrors] = useState({});

  // Track if form has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialFormState, setInitialFormState] = useState('loading'); // Start with loading state

  // Get the navigate function
  const navigate = useNavigate();

  // Check if form has been modified
  useEffect(() => {
    if (initialFormState && initialFormState !== 'loading') {
      const currentState = JSON.stringify({
        formData,
        products,
        selectedClientId,
        selectedClientData,
        pricingMode
      });
      const hasChanges = currentState !== initialFormState;
      setHasUnsavedChanges(hasChanges);
    }
  }, [formData, products, selectedClientId, selectedClientData, pricingMode, initialFormState]);

  // Initialize initial state when component mounts or data loads
  const initializeFormState = () => {
    const state = JSON.stringify({
      formData,
      products,
      selectedClientId,
      selectedClientData,
      pricingMode
    });
    setInitialFormState(state);
    setHasUnsavedChanges(false);
  };

  // Save function for the navigation blocker - memoized to prevent re-registration
  const saveForNavigationBlock = useCallback(async () => {
    // Directly call handleSubmit instead of dispatching form event
    const fakeEvent = { preventDefault: () => {} };
    return handleSubmit(fakeEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps to keep function stable

  // Memoize navigation blocker options to prevent re-registration
  const navigationBlockerOptions = useMemo(() => ({
    title: '未保存的销售订单',
    message: '您有未保存的销售订单更改。离开此页面将丢失这些更改。',
    enabled: true
  }), []);

  // Register the navigation blocker (only when not loading and form is initialized)
  useBlockNavigation(
    hasUnsavedChanges && !loading && initialFormState && initialFormState !== 'loading',
    saveForNavigationBlock,
    navigationBlockerOptions
  );

  // Standardized field mapping utility
  const mapClientDataToForm = (clientData) => ({
    name: clientData.name || clientData.client_name || '',
    client_name: clientData.name || clientData.client_name || '',
    contact_person: clientData.contact_person || clientData.client_contact || '',
    email: clientData.email || clientData.client_email || '',
    phone: clientData.phone || clientData.client_phone || '',
    address: clientData.address || clientData.client_address || '',
    bank_name: clientData.bank_name || '',
    bank_account: clientData.bank_account || '',
    tax_registration_number: clientData.tax_registration_number || '',
    isLegacy: clientData.isLegacy || false
  });

  // Handle client selection
  const handleClientChange = (clientId, clientData) => {
    setSelectedClientId(clientId);
    setSelectedClientData(clientData ? mapClientDataToForm(clientData) : null);
  };

  // Form steps configuration

  // Load existing sales order for editing
  useEffect(() => {
    const loadOrder = async () => {
      try {
        setLoading(true);
        const order = await salesService.getSalesOrder(id);

        // Check client access for confidentiality
        const isAdmin = authService.isAdmin();
        if (order.client_id && !isAdmin) {
          try {
            // Fetch the client to check if it's confidential
            const clientResponse = await clientService.getClient(order.client_id);
            const client = clientResponse.client || clientResponse;

            if (client?.is_confidential) {
              setError('无权访问：此销售订单属于保密客户');
              setTimeout(() => navigate('/sales'), 2000);
              return;
            }
          } catch (err) {
            console.warn('Could not verify client access:', err);
            // Continue loading if we can't verify (backend will enforce)
          }
        }

        // Handle client data properly
        if (order.client_id) {
          // Real client ID - set both ID and data for immediate display
          setSelectedClientId(order.client_id);
          // Keep client data from order for display while ClientSelector loads full data
          setSelectedClientData({
            name: order.client_name || '',
            client_name: order.client_name || '',
            contact_person: order.client_contact || '',
            email: order.client_email || '',
            phone: order.client_phone || '',
            address: order.client_address || '',
            bank_name: order.bank_name || '',
            bank_account: order.bank_account || '',
            tax_registration_number: order.tax_registration_number || ''
          });
        } else if (order.client_name) {
          // Legacy data - create a proper legacy structure using standardized mapping
          setSelectedClientId('legacy-' + id);
          setSelectedClientData(mapClientDataToForm({
            client_name: order.client_name,
            client_contact: order.client_contact,
            client_email: order.client_email,
            client_phone: order.client_phone,
            client_address: order.client_address,
            bank_name: order.bank_name,
            bank_account: order.bank_account,
            tax_registration_number: order.tax_registration_number,
            isLegacy: true
          }));
        } else {
          // No client data at all
          setSelectedClientId(null);
          setSelectedClientData(null);
        }

        setFormData({
          sales_order_id: order.sales_order_id || order.order_id || `SO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          order_date: order.order_date ? order.order_date.split('T')[0] : '',
          delivery_date: order.delivery_date ? order.delivery_date.split('T')[0] : '',
          payment_terms: order.payment_terms || '',
          notes: order.notes || '',
          status: order.status || 'draft'
        });

        // Initialize pricing mode if present
        if (order.pricing_mode) {
          setPricingMode(order.pricing_mode);
        }

        // Ensure loaded products have both price and unit_price fields and preserve _id
        const normalizedProducts = (order.products || []).map(product => ({
          ...product,
          _id: product._id, // Preserve MongoDB ID for attachment lookup
          unit_price: product.unit_price || product.price || 0,
          price: product.price || product.unit_price || 0,
          unit_price_tax_included: product.unit_price_tax_included ||
            (product.unit_price ? product.unit_price * 1.13 : (product.price ? product.price * 1.13 : 0))
        }));
        setProducts(normalizedProducts);

        // Initialize form state after loading data
        setTimeout(() => {
          initializeFormState();
        }, 100); // Small delay to ensure state updates have propagated
      } catch (err) {
        setError('Failed to load sales order');
        console.error('Load sales order error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isEditMode && id) {
      loadOrder();
    } else if (isCloneMode && location.state?.clonedOrder) {
      // Handle cloned order data
      const clonedOrder = location.state.clonedOrder;
      
      // Handle client data
      if (clonedOrder.client_id) {
        setSelectedClientId(clonedOrder.client_id);
        setSelectedClientData({
          name: clonedOrder.client_name || '',
          client_name: clonedOrder.client_name || '',
          contact_person: clonedOrder.client_contact || '',
          email: clonedOrder.client_email || '',
          phone: clonedOrder.client_phone || '',
          address: clonedOrder.client_address || '',
          bank_name: clonedOrder.bank_name || '',
          bank_account: clonedOrder.bank_account || '',
          tax_registration_number: clonedOrder.tax_registration_number || ''
        });
      }
      
      // Set form data with new order ID
      setFormData({
        sales_order_id: `SO-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
        order_date: new Date().toISOString().split('T')[0],
        delivery_date: clonedOrder.delivery_date ? clonedOrder.delivery_date.split('T')[0] : '',
        payment_terms: clonedOrder.payment_terms || '',
        notes: clonedOrder.notes || '',
        status: 'draft'
      });
      
      // Set pricing mode
      if (clonedOrder.pricing_mode) {
        setPricingMode(clonedOrder.pricing_mode);
      }
      
      // Set products (clean copies without IDs)
      const normalizedProducts = (clonedOrder.products || []).map(product => ({
        ...product,
        _id: undefined,
        unit_price: product.unit_price || product.price || 0,
        price: product.price || product.unit_price || 0,
        unit_price_tax_included: product.unit_price_tax_included ||
          (product.unit_price ? product.unit_price * 1.13 : (product.price ? product.price * 1.13 : 0))
      }));
      setProducts(normalizedProducts);
      
      // Initialize form state
      setTimeout(() => {
        initializeFormState();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id, isCloneMode, location.state]);

  // Initialize form state when component mounts (for new forms)
  useEffect(() => {
    if (!isEditMode && !isCloneMode) {
      // For new forms, initialize immediately
      setTimeout(() => {
        initializeFormState();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch attachment counts when products change
  useEffect(() => {
    const fetchAttachmentCounts = async () => {
      const productIds = products
        .map(p => p._id)
        .filter(id => id); // Filter out products without _id

      if (productIds.length > 0) {
        try {
          const counts = await fileService.getProductsAttachmentCounts(productIds);
          setAttachmentCounts(counts);
        } catch (error) {
          console.error('Failed to load attachment counts:', error);
          setAttachmentCounts({});
        }
      } else {
        setAttachmentCounts({});
      }
    };

    if (products.length > 0) {
      fetchAttachmentCounts();
    } else {
      setAttachmentCounts({});
    }
  }, [products]);

  // Calculate form completion percentage
  const getFormCompletion = () => {
    const requiredFields = ['order_date'];
    const filledFields = requiredFields.filter(field => formData[field]?.trim());
    const hasProducts = products.length > 0;
    const baseCompletion = (filledFields.length / requiredFields.length) * 70;
    const productCompletion = hasProducts ? 30 : 0;
    return Math.round(baseCompletion + productCompletion);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'client_email':
        return value && !/\S+@\S+\.\S+/.test(value) ? '邮箱格式不正确' : '';
      case 'client_phone':
        // Support both mobile (1xxxxxxxxxx) and landline (xxx-xxxxxxxx or xxxxxxxxx) formats
        return value && !/^([1][3-9]\d{9}|0\d{2,3}-?\d{7,8}|\d{7,8})$/.test(value) ? '电话号码格式不正确' : '';
      default:
        return '';
    }
  };


  const handleAddProducts = (selectedProducts) => {
    setProducts(prev => {
      const updatedProducts = [...prev];

      selectedProducts.forEach(selectedProduct => {
        // 不合并重复项目，每个产品都作为独立行添加
        updatedProducts.push({
          _id: selectedProduct._id, // Preserve MongoDB ID for attachment lookup
          line_item_id: crypto.randomUUID(), // Unique identifier for this line item
          product_id: selectedProduct.product_id,
          name: selectedProduct.name,
          specification: selectedProduct.specification,
          // Manufacturing parameters
          material: selectedProduct.material || '',
          coating: selectedProduct.coating || '',
          tip_diameter: selectedProduct.tip_diameter || '',
          total_length: selectedProduct.total_length || '',
          degree: selectedProduct.degree || '',
          shank_diameter: selectedProduct.shank_diameter || '',
          tolerance: selectedProduct.tolerance || '',
          blade_type: selectedProduct.blade_type || '',
          guide_pillar_length: selectedProduct.guide_pillar_length || '',
          guide_pillar_tolerance: selectedProduct.guide_pillar_tolerance || '',
          marking: selectedProduct.marking || '',
          // Order fields
          quantity: 1,
          unit_price: selectedProduct.price || 0,
          price: selectedProduct.price || 0,
          unit_price_tax_included: selectedProduct.price ? selectedProduct.price * 1.13 : 0
        });
      });

      return updatedProducts;
    });

    setProductDialogOpen(false);
  };

  const handleReplaceProduct = (index, currentProduct) => {
    setReplaceDialogState({
      open: true,
      replacingIndex: index,
      currentProduct: currentProduct
    });
  };

  const handleReplaceConfirm = (selectedProducts) => {
    if (selectedProducts.length === 0) return;

    const newProduct = selectedProducts[0]; // Take first selected product
    const updatedProducts = [...products];
    const oldProduct = products[replaceDialogState.replacingIndex];
    const oldQuantity = oldProduct.quantity;

    // Replace product while preserving quantity and line_item_id
    updatedProducts[replaceDialogState.replacingIndex] = {
      ...newProduct,
      _id: newProduct._id,
      line_item_id: oldProduct.line_item_id || crypto.randomUUID(), // Preserve or generate line_item_id
      product_mongo_id: newProduct._id,
      quantity: oldQuantity, // Preserve original quantity
      unit_price: newProduct.price || 0,
      price: newProduct.price || 0,
      unit_price_tax_included: newProduct.price ? newProduct.price * 1.13 : 0
    };

    setProducts(updatedProducts);
    setReplaceDialogState({ open: false, replacingIndex: null, currentProduct: null });
  };

  const removeProduct = (lineItemId) => {
    setProducts(prev => prev.filter(p => p.line_item_id !== lineItemId));
  };

  const calculateTotal = () => {
    const total = products.reduce((sum, product) => {
      let itemTotal;
      if (pricingMode === 'tax_inclusive') {
        // Round line item total to 2 decimal places
        itemTotal = product.total_price_tax_included ?? Math.round(((product.unit_price_tax_included ?? (product.unit_price ?? 0) * 1.13) * (product.quantity ?? 0)) * 100) / 100;
      } else { // pricingMode === 'tax_exclusive'
        // Round line item total to 2 decimal places
        itemTotal = product.total_price ?? Math.round(((product.unit_price ?? 0) * (product.quantity ?? 0)) * 100) / 100;
      }
      return sum + (itemTotal || 0);
    }, 0);
    return Math.round(total * 100) / 100;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const errors = {};
    if (!selectedClientId || (!selectedClientData?.name && !selectedClientData?.client_name)) {
      errors.client_name = '请选择客户';
    }
    if (formData.client_email) {
      const emailError = validateField('client_email', formData.client_email);
      if (emailError) errors.client_email = emailError;
    }
    if (formData.client_phone) {
      const phoneError = validateField('client_phone', formData.client_phone);
      if (phoneError) errors.client_phone = phoneError;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('请检查表单中的错误信息');
      return;
    }

    if (products.length === 0) {
      setError('请至少添加一个产品');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const orderData = {
        ...formData,
        client_id: selectedClientId,
        client_ref: selectedClientId,  // Send client_ref as ObjectId string for backend normalization
        client_name: selectedClientData?.name || selectedClientData?.client_name || '',
        client_contact: selectedClientData?.contact_person || '',
        client_email: selectedClientData?.email || '',
        client_phone: selectedClientData?.phone || '',
        client_address: selectedClientData?.address || '',
        bank_name: selectedClientData?.bank_name || '',
        bank_account: selectedClientData?.bank_account || '',
        tax_registration_number: selectedClientData?.tax_registration_number || '',
        products: products.map(p => ({
          _id: p._id, // Include MongoDB _id for product reference
          product_id: p.product_id,
          name: p.name,
          specification: p.specification,
          client_ref_number: p.client_ref_number, // Include for variant matching
          quantity: p.quantity,
          price: p.unit_price,
          unit_price: p.unit_price,
          unit_price_tax_included: p.unit_price_tax_included || (p.unit_price || 0) * 1.13,
          total_price: (() => {
            let itemTotalBeforeRounding;
            if (pricingMode === 'tax_inclusive') {
              itemTotalBeforeRounding = (p.unit_price_tax_included ?? ((p.unit_price || 0) * 1.13)) * (p.quantity || 0);
            } else { // pricingMode === 'tax_exclusive'
              itemTotalBeforeRounding = (p.unit_price || 0) * (p.quantity || 0);
            }
            return Math.round(itemTotalBeforeRounding * 100) / 100;
          })(),
          // Manufacturing parameters
          material: p.material,
          coating: p.coating,
          diameter: p.diameter,
          length: p.length,
          shank_diameter: p.shank_diameter,
          tolerance: p.tolerance,
          blade_type: p.blade_type,
          guide_pillar_length: p.guide_pillar_length,
          guide_pillar_tolerance: p.guide_pillar_tolerance,
          marking: p.marking
        })),
        pricing_mode: pricingMode,
        total_price: calculateTotal()
      };

      if (isEditMode) {
        await salesService.updateSalesOrder(id, orderData);
        setSuccess('销售订单更新成功！');
        initializeFormState(); // Reset form state after successful save
      } else {
        await salesService.createSalesOrder(orderData);
        setSuccess('销售订单创建成功！');
        setHasUnsavedChanges(false); // Clear unsaved changes flag
        // Only navigate away for new orders
        setTimeout(() => {
          navigate('/sales');
        }, 1500);
      }

    } catch (err) {
      setError(err.response?.data?.error || '保存销售订单失败');
      console.error('Save sales order error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToPurchase = async () => {
    try {
      setConverting(true);
      setError('');

      await purchaseService.convertSalesOrderToPurchase(id);
      setSuccess('销售订单已成功转换为采购订单！');

      setTimeout(() => {
        navigate('/purchases');
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.error || '转换采购订单失败');
      console.error('Convert to purchase error:', err);
    } finally {
      setConverting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  const handleAttachmentClick = (product) => {
    if (!product._id) {
      console.warn('Product missing MongoDB _id, cannot view attachments');
      return;
    }
    setAttachmentDialog({
      open: true,
      product: product
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'confirmed': return 'bg-primary/10 text-primary border-primary/30';
      case 'in_progress': return 'bg-warning/10 text-warning border-warning/30';
      case 'completed': return 'bg-success/10 text-success border-success/30';
      case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return '草稿';
      case 'confirmed': return '已确认';
      case 'in_progress': return '进行中';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return '草稿';
    }
  };

  if (loading) {
    return (
      <Layout title={isEditMode ? "编辑销售订单" : "新建销售订单"} activeTab="sales">
        <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="mt-4 text-lg text-muted-foreground">
                正在加载订单信息...
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEditMode ? "编辑销售订单" : "新建销售订单"} activeTab="sales">
      <div className="max-w-5xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header Section */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/sales')}
            className="mb-4 text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回销售列表
          </Button>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">
                {isEditMode ? "编辑销售订单" : "新建销售订单"}
              </h1>
              <p className="text-muted-foreground">
                {isEditMode ? "修改现有销售订单信息" : "创建新的销售订单"}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {isEditMode && (
                <Badge className={cn('border', getStatusClass(formData.status))}>
                  {getStatusText(formData.status)}
                </Badge>
              )}
              <div className="text-center">
                <p className="text-xs text-muted-foreground">完成度</p>
                <p className="text-lg font-semibold text-primary">{getFormCompletion()}%</p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full mb-6">
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300 ease-in-out rounded-full"
                style={{ width: `${getFormCompletion()}%` }}
              />
            </div>
          </div>
        </div>

        {/* Alert Messages */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Enhanced Success Toast Notification */}
        {success && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50">
            <Alert className="bg-success text-success-foreground border-success shadow-lg min-w-[400px]">
              <CheckCircle className="h-5 w-5" />
              <AlertDescription className="flex items-center justify-between font-semibold">
                <span>{success}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-success-foreground hover:bg-success/80"
                  onClick={() => setSuccess('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-8">
            {/* Customer Information Section */}
            <Card className="border bg-card overflow-visible relative hover:shadow-md hover:border-primary/50 transition-all duration-300">
              {/* Section Header */}
              <div className="absolute -top-3 left-6 bg-background px-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                  <User className="h-3 w-3 text-primary-foreground" />
                </div>
                <span className="text-base font-semibold text-primary">客户信息</span>
              </div>

              <CardContent className="pt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <ClientSelector
                      selectedClientId={selectedClientId}
                      onClientChange={handleClientChange}
                      selectedClientName={selectedClientData?.name || selectedClientData?.client_name || ''}
                      required
                      label="客户名称"
                      placeholder="搜索或选择客户"
                      helperText="选择客户后将自动填充客户信息"
                      size="medium"
                      compact={false}
                    />
                  </div>
                  {selectedClientData && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">联系人</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{selectedClientData.contact_person || '-'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">邮箱</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{selectedClientData.email || '-'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">电话</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{selectedClientData.phone || '-'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">开户行</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{selectedClientData.bank_name || '-'}</span>
                        </div>
                      </div>
                      <div className="md:col-span-2 space-y-2">
                        <Label className="text-sm text-muted-foreground">客户地址</Label>
                        <div className="flex items-start gap-2 px-3 py-2 bg-muted/50 rounded-md border min-h-[60px]">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <span className="text-sm">{selectedClientData.address || '-'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">银行账户</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                          <span className="text-sm">{selectedClientData.bank_account || '-'}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">税务登记号</Label>
                        <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border">
                          <span className="text-sm">{selectedClientData.tax_registration_number || '-'}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Information Section */}
            <Card className="border bg-card overflow-visible relative hover:shadow-md hover:border-primary/50 transition-all duration-300">
              {/* Section Header */}
              <div className="absolute -top-3 left-6 bg-background px-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center">
                  <Building2 className="h-3 w-3 text-secondary-foreground" />
                </div>
                <span className="text-base font-semibold text-secondary">订单信息</span>
              </div>

              <CardContent className="pt-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sales_order_id">订单编号</Label>
                    <div className="relative">
                      <ShoppingCart className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="sales_order_id"
                        name="sales_order_id"
                        value={formData.sales_order_id}
                        onChange={handleInputChange}
                        placeholder="输入订单编号"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">可自定义订单编号</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="order_date">订单日期 *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="order_date"
                        name="order_date"
                        type="date"
                        value={formData.order_date}
                        onChange={handleInputChange}
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_date">交付日期</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="delivery_date"
                        name="delivery_date"
                        type="date"
                        value={formData.delivery_date}
                        onChange={handleInputChange}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment_terms">付款条款</Label>
                    <Input
                      id="payment_terms"
                      name="payment_terms"
                      value={formData.payment_terms}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="md:col-span-3 space-y-2">
                    <Label htmlFor="notes">备注</Label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Textarea
                        id="notes"
                        name="notes"
                        rows={4}
                        value={formData.notes}
                        onChange={handleInputChange}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products Section */}
            <Card className="border bg-card overflow-visible relative hover:shadow-md hover:border-primary/50 transition-all duration-300">
              {/* Section Header */}
              <div className="absolute -top-3 left-6 bg-background px-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-success flex items-center justify-center">
                  <ShoppingCart className="h-3 w-3 text-success-foreground" />
                </div>
                <span className="text-base font-semibold text-success">产品列表</span>
              </div>

              <CardContent className="pt-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      已添加 {products.length} 个产品
                    </span>
                    {products.length > 0 && (
                      <Badge variant="outline" className={cn(
                        pricingMode === 'tax_inclusive' ? 'border-primary text-primary' : ''
                      )}>
                        总计({pricingMode === 'tax_inclusive' ? '' : '不'}含税): {formatCurrency(calculateTotal())}
                      </Badge>
                    )}
                  </div>
                  <div className="min-w-[220px]">
                    <PricingModeSelector
                      value={pricingMode}
                      onChange={setPricingMode}
                      size="small"
                      variant="compact"
                    />
                  </div>
                  <div className="flex gap-2">
                    {isEditMode && products.length > 0 && (
                      <SyncProductsButton
                        onPreview={async () => {
                          // Preview changes (NO database modification)
                          const preview = await salesService.previewSyncSalesOrderProducts(id);
                          return preview;
                        }}
                        onApply={async () => {
                          // Apply changes after user approval
                          const result = await salesService.syncSalesOrderProducts(id);
                          // Reload the order to show updated products
                          if (result.updated_count > 0) {
                            const updatedOrder = await salesService.getSalesOrder(id);
                            const normalizedProducts = (updatedOrder.products || []).map(product => ({
                              ...product,
                              unit_price: product.unit_price || product.price || 0,
                              price: product.price || product.unit_price || 0,
                              unit_price_tax_included: product.unit_price_tax_included ||
                                (product.unit_price ? product.unit_price * 1.13 : 0)
                            }));
                            setProducts(normalizedProducts);
                          }
                          return result;
                        }}
                        variant="outlined"
                        size="medium"
                      />
                    )}
                    <Button onClick={() => setProductDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      添加产品
                    </Button>
                  </div>
                </div>

                <ProductTable
                  products={products}
                  view="sales"
                  onEdit={(product) => {
                    console.log('Edit product:', product);
                  }}
                  onDelete={(lineItemId) => {
                    // lineItemId is the line_item_id passed from ProductTableRow
                    if (lineItemId) {
                      removeProduct(lineItemId);
                    }
                  }}
                  onPriceChange={(index, newPrice) => {
                    const updatedProducts = [...products];
                    const priceWithTax = newPrice * 1.13; // Calculate tax inclusive price (no rounding on unit price)
                    updatedProducts[index] = {
                      ...updatedProducts[index],
                      unit_price: newPrice,
                      price: newPrice,
                      unit_price_tax_included: priceWithTax,
                      pricing_mode: 'tax_exclusive'
                    };
                    setProducts(updatedProducts);
                  }}
                  onPriceTaxIncludedChange={(index, newPriceWithTax) => {
                    const priceExcludingTax = newPriceWithTax / 1.13; // Remove 13% tax (no rounding on unit price)
                    const updatedProducts = [...products];
                    updatedProducts[index] = {
                      ...updatedProducts[index],
                      unit_price: priceExcludingTax,
                      price: priceExcludingTax,
                      unit_price_tax_included: newPriceWithTax,
                      pricing_mode: 'tax_inclusive'
                    };
                    setProducts(updatedProducts);
                  }}
                  onQuantityChange={(index, newQuantity) => {
                    const updatedProducts = [...products];
                    updatedProducts[index] = { ...updatedProducts[index], quantity: newQuantity };
                    setProducts(updatedProducts);
                  }}
                  onPriceLookup={(index, product) => {
                    console.log('Price lookup for product:', product, 'at index:', index);
                    if (product && product.product_id) {
                      setPriceHistoryDrawer({
                        open: true,
                        productId: product.product_id || '',
                        productName: product.name || '',
                        tipDiameter: product.tip_diameter || '',
                        totalLength: product.total_length || '',
                        category: product.category || '',
                        lineItemIndex: index // Store the line item index for correct price application
                      });
                    } else {
                      alert('无法查询价格历史：产品编号缺失');
                    }
                  }}
                  onReplace={handleReplaceProduct}
                  onReorder={(reorderedProducts) => {
                    setProducts(reorderedProducts);
                  }}
                  readOnly={false}
                  title="销售订单产品列表"
                  hasAttachments={(product) => (attachmentCounts[product._id] || 0) > 0}
                  onAttachmentClick={handleAttachmentClick}
                  canViewPrices={authService.canViewSalesPrices()}
                />

              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div>
              <Separator className="mb-6" />
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                {/* Left side - Export and Convert buttons */}
                <div className="flex gap-2 items-center">
                  {/* Export Button - Only show for edit mode with products */}
                  {isEditMode && products.length > 0 && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setTemplateSelectorOpen(true)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      导出到Excel
                    </Button>
                  )}

                  {/* Convert to Purchase Button - Only show for confirmed orders */}
                  {isEditMode && formData.status === 'confirmed' && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleConvertToPurchase}
                      disabled={converting || saving}
                      className="border-secondary text-secondary hover:bg-secondary/10"
                    >
                      {converting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ShoppingBag className="h-4 w-4 mr-2" />
                      )}
                      {converting ? '转换中...' : '转换为采购订单'}
                    </Button>
                  )}
                </div>

                {/* Main action buttons */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/sales')}
                    disabled={saving || converting}
                  >
                    取消
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving || converting}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    {saving ? '保存中...' : (isEditMode ? '更新订单' : '创建订单')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Enhanced Product Selection Dialog */}
        <EnhancedProductLookup
          open={productDialogOpen}
          onClose={() => setProductDialogOpen(false)}
          onAddProducts={handleAddProducts}
          clientName={selectedClientData?.name || selectedClientData?.client_name || ''}
          currentQuotationItems={products}
        />

        {/* Replace Product Dialog */}
        <EnhancedProductLookup
          open={replaceDialogState.open}
          onClose={() => setReplaceDialogState({ open: false, replacingIndex: null, currentProduct: null })}
          onAddProducts={handleReplaceConfirm}
          clientName={selectedClientData?.name || selectedClientData?.client_name || ''}
          currentQuotationItems={products}
        />

        {/* Price History Drawer */}
        <PriceHistoryDrawer
          open={priceHistoryDrawer.open}
          onClose={() => setPriceHistoryDrawer({ open: false, productId: '', productName: '', tipDiameter: '', totalLength: '', category: '', lineItemIndex: null })}
          initialMode="single"
          productId={priceHistoryDrawer.productId}
          productName={priceHistoryDrawer.productName}
          initialTipDiameter={priceHistoryDrawer.tipDiameter}
          initialTotalLength={priceHistoryDrawer.totalLength}
          initialCategory={priceHistoryDrawer.category}
          initialClientName={selectedClientData?.name || selectedClientData?.client_name || ''}
          showActions={true}
          onApplyPrice={(priceData) => {
            // Use lineItemIndex to update the correct line item (handles same product_id with different specs)
            const productIndex = priceHistoryDrawer.lineItemIndex;
            if (productIndex !== null && productIndex >= 0 && productIndex < products.length) {
              const updatedProducts = [...products];
              const exclusive = (typeof priceData === 'number')
                ? priceData
                : (
                    priceData?.tax_exclusive_price
                    ?? priceData?.price
                    ?? priceData?.unit_price
                    ?? priceData?.base_price
                    ?? (
                      typeof priceData === 'object' && typeof priceData?.tax_inclusive_price === 'number'
                        ? (priceData.tax_inclusive_price / 1.13)
                        : 0
                    )
                  );
              const inclusive = (typeof priceData === 'number')
                ? (priceData * 1.13)
                : (
                    priceData?.tax_inclusive_price
                    ?? (
                      typeof priceData === 'object' && typeof priceData?.tax_exclusive_price === 'number'
                        ? (priceData.tax_exclusive_price * 1.13)
                        : 0
                    )
                  );

              updatedProducts[productIndex] = {
                ...updatedProducts[productIndex],
                unit_price: exclusive,
                price: exclusive,
                unit_price_tax_included: inclusive || (exclusive * 1.13),
                pricing_mode: pricingMode
              };
              setProducts(updatedProducts);
            }
          }}
        />

        {/* Template Selector Dialog */}
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={() => setTemplateSelectorOpen(false)}
          entityType="sales"
          onSelect={async (template) => {
            try {
              const { blob, filename } = await excelExportService.exportUsingTemplate('sales', {
                template_id: template.template_id,
                sales_order_ids: [id]
              });

              excelExportService.downloadBlob(blob, filename);
              console.log('Export completed:', filename);
              setSuccess('Export completed successfully');
            } catch (error) {
              console.error('Export failed:', error);
              setError('Failed to export data: ' + error.message);
            }
          }}
        />

        {/* Product Attachment Preview Dialog */}
        <ProductAttachmentPreviewDialog
          open={attachmentDialog.open}
          onClose={() => setAttachmentDialog({ open: false, product: null })}
          product={attachmentDialog.product}
          productMongoId={attachmentDialog.product?._id}
        />
      </div>
    </Layout>
  );
};

export default SalesFormPage;
